import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import NumberSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/NumberSetting';
import Padding from 'hb-builder/components/common/SettingsLibrary/CommonSettings/Padding';
import { PADDING_INPUT_SET_TYPES } from 'hb-builder/components/common/SettingsLibrary/CommonSettings/StylingMode/CustomMode/ButtonStyle/constants/paddings';
import { getDisplayedPaddingInputs } from 'hb-builder/components/common/SettingsLibrary/CommonSettings/StylingMode/CustomMode/ButtonStyle/utils/paddings';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { useBuilderConfigContext } from 'hb-builder/hooks';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';
import type { ButtonItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';
import type { Color } from 'hb-react/types';

import Borders from './Borders';

const ButtonStyle = ({
  defaultAttributes,
  displayedPaddingInputs = PADDING_INPUT_SET_TYPES.ALL,
  item,
  onChange,
  showColorOnlyForBorders,
}: ComponentSettingsTab<ButtonItemAttributes> & {
  displayedPaddingInputs?: PADDING_INPUT_SET_TYPES;
  showColorOnlyForBorders?: boolean;
}) => {
  const {
    features: { color },
    hivebrite: { networkColors, networkTheming },
  } = useBuilderConfigContext();

  if (!item) {
    return null;
  }

  const colorSettingsProps = {
    color,
    networkColors,
    networkTheming,
  };

  const attributes = item?.attributes;

  const isBtnSecondary = attributes?.variant === 'secondary';
  const defaultSecondaryBgColor = networkColors?.button_secondary;
  const defaultPrimaryBgColor = networkColors?.button_primary;

  const defaultBgColor = isBtnSecondary
    ? defaultSecondaryBgColor
    : defaultPrimaryBgColor;

  const settingProps = {
    defaultAttributes: {
      ...defaultAttributes,
      buttonBackgroundColor:
        defaultAttributes?.buttonBackgroundColor || (defaultBgColor as Color),
    },
    item,
    onChange,
  };

  return (
    <StyleWrapper>
      <InputTitle boxProps={{ mb: 4 }} label={__('Button style')} />
      <ColorSetting
        settingKey="buttonBackgroundColor"
        title={__('Button color')}
        transparent={false}
        {...settingProps}
        {...colorSettingsProps}
      />
      <Borders
        {...settingProps}
        {...colorSettingsProps}
        colorOnly={showColorOnlyForBorders}
      />
      <NumberSetting
        settingKey="buttonRadius"
        title={__('Rounded corners')}
        {...settingProps}
      />
      <Padding
        needResetAlignment={false}
        settingKey="buttonPadding"
        title={__('Button paddings (in pixels)')}
        // @ts-expect-error Padding component is not typed
        displayedInputs={getDisplayedPaddingInputs(displayedPaddingInputs)}
        {...settingProps}
      />
    </StyleWrapper>
  );
};

export default ButtonStyle;

const StyleWrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  width: '100%',
}))``;
